/** New license classes (Luật TTATGT 2024) */
export type LicenseClass =
  | 'A.01' | 'A.02' | 'A.03' | 'A11' | 'A1m' | 'Am' | 'A4'
  | 'B' | 'B.01' | 'B.02' | 'B.03' | 'B.04' | 'B.05' | 'B1m'
  | 'C1' | 'C1E' | 'Cm'
  | 'D1' | 'D1E' | 'D2' | 'D2E' | 'Dm' | 'DE'
  | 'BE' | 'CE';

/**
 * Hạng mà trung tâm đang đào tạo — dùng để lọc dropdown Tạo/Sửa khóa,
 * dropdown giáo viên chọn khi nộp hồ sơ, và filter hàng đợi NVTT.
 *
 * Sửa danh sách này ở 1 chỗ là toàn hệ thống đồng bộ.
 */
export const CENTER_ACTIVE_HANGS: Array<{
  value: LicenseClass;
  label: string;
  desc: string;
  /** Mark a hạng as nâng hạng — drives required-GPLX validation in
   *  the scanner form. Hạng without this flag still ALLOWS the
   *  operator to enter GPLX info, just doesn't require it. */
  isNangHang?: boolean;
}> = [
  { value: 'Am',   label: 'Am',   desc: 'Hạng A mới (Am)' },
  { value: 'A1m',  label: 'A1m',  desc: 'Hạng A1 mới (A1m)' },
  { value: 'B',    label: 'B',    desc: 'Hạng B (số sàn)' },
  { value: 'B.01', label: 'B.01', desc: 'Hạng B.01 (số tự động)' },
  { value: 'C1',   label: 'C1',   desc: 'Hạng C1' },
  { value: 'Cm',   label: 'Cm',   desc: 'Hạng Cm (C mới)',           isNangHang: true },
  { value: 'CE',   label: 'CE',   desc: 'Nâng hạng đầu kéo (CE)',    isNangHang: true },
];

/** Value-only array for dropdown rendering. */
export const CENTER_ACTIVE_HANG_VALUES: LicenseClass[] = CENTER_ACTIVE_HANGS.map(h => h.value);

export type CourseStatus = 'open' | 'pending_entry' | 'closed';

export type CsdtStatus = 'none' | 'entered' | 'verified' | 'completed' | 'failed';

export type DvhcStatus = 'pending' | 'converted' | 'failed';

export type ScanMethod = 'qr' | 'ocr' | 'manual';

export type TtCourse = {
  id: number;
  name: string;
  hang: LicenseClass;
  startDate: string | null;
  endDate: string | null;
  status: CourseStatus;
  isNangHang: boolean;
  /** Giới hạn số hồ sơ được duyệt. null = không giới hạn. */
  maxReviewed: number | null;
  createdBy: number | null;
  creator?: { id: number; username: string } | null;
  recordCount: number;
  reviewedCount: number;
  enteredCsdtCount: number;
  /** Số hồ sơ đã duyệt nhưng nhập CSĐT bị lỗi (csdtStatus='failed'). */
  csdtFailedCount?: number;
  /** Số hồ sơ đang chờ lưu (tt_pending_record) thuộc khóa này. */
  pendingCount?: number;
  createdAt: string;
  updatedAt: string;
};

export type DocType = 'cccd' | 'gplx' | 'health' | 'other';

export type TtStudentDocument = {
  id: number;
  recordId: number;
  docType: DocType;
  label: string | null;
  fileUrl: string;
  fileBase64?: string | null;
  uploadedBy: number | null;
  uploader?: { id: number; username: string } | null;
  createdAt: string;
  updatedAt: string;
};

export type TtStudentRecord = {
  id: number;
  ttCourseId: number;
  soCCCD: string;
  soCMT: string | null;
  hoTen: string;
  ngaySinh: string | null;
  gioiTinh: string | null;
  diaChi: string | null;
  diaChiCap2: string | null;
  ngayCap: string | null;
  avatarUrl: string | null;
  sourceImageUrl: string | null;
  avatarBase64?: string | null;
  sourceImageBase64?: string | null;
  scanMethod: ScanMethod;
  enteredBy: number | null;
  reviewed: boolean;
  csdtStatus: CsdtStatus;
  soGPLX: string | null;
  hangGPLX: string | null;
  hangGPLXMoi: string | null;
  ngayCapGPLX: string | null;
  noiCapGPLX: string | null;
  donViCapGPLX: string | null;
  ngayTrungTuyenGPLX: string | null;
  ngayHetHanGPLX: string | null;
  maDvhc: string | null;
  dvhcTenDayDu: string | null;
  dvhcStatus: DvhcStatus;
  dvhcError: string | null;
  dvhcInfo?: { tenDayDu: string } | null;
  // Optional portrait photo (ảnh thẻ) — separate from avatarBase64 which
  // is cropped from the scanned CCCD. Set via the upload flow in the edit
  // modal. faceMatchScore is 0-100 similarity vs the CCCD avatar.
  portraitBase64?: string | null;
  /** Raw selfie the operator uploaded BEFORE AI enhance. Kept alongside
   *  portraitBase64 so the review UI can show before/after side-by-side. */
  portraitOriginalBase64?: string | null;
  /** JSON array of ALL AI-generated portrait variants for this record.
   *  User can gen multiple times; each result appended here. One of
   *  them may be selected as `portraitBase64` (the active avatar). */
  portraitAiVariantsJson?: string | null;
  faceMatchScore?: number | null;
  faceMatchAt?: string | null;
  enteredByUser?: { id: number; username: string } | null;
  teacher?: { id: number; username: string; email: string } | null;
  documents?: TtStudentDocument[];
  /** Extra images (GPLX back, health cert, misc) attached during the
   *  pending-review phase. Carried over from pending record on save;
   *  persists with the record until deletion. Stored server-side as
   *  a JSON string; the list API parses it into this field. */
  extraImagesJson?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TtCourseFormData = {
  name: string;
  hang: LicenseClass;
  startDate?: string;
  endDate?: string;
  isNangHang?: boolean;
  /** Quota — null/0/undefined = không giới hạn. */
  maxReviewed?: number | null;
};

export type DvhcConvertResult = {
  attempted: number;
  converted: number;
  failed: number;
  skipped: number;
  remainingBalance?: number | null;
};

export type DvhcConvertStatus = {
  busy: boolean;
  retryAfterSec: number;
};

export type TtRecordFormData = {
  ttCourseId: number;
  soCCCD: string;
  soCMT?: string;
  hoTen: string;
  ngaySinh?: string;
  gioiTinh?: string;
  diaChi?: string;
  diaChiMoi?: string;
  diaChiCap2?: string;
  ngayCap?: string;
  avatarUrl?: string;
  sourceImageBase64?: string;
  scanMethod: ScanMethod;
  soGPLX?: string;
  hangGPLX?: string;
  hangGPLXMoi?: string;
  ngayCapGPLX?: string;
  noiCapGPLX?: string;
  donViCapGPLX?: string;
  ngayTrungTuyenGPLX?: string;
  ngayHetHanGPLX?: string;
  maDvhc?: string;
  dvhcTenDayDu?: string;
  portraitBase64?: string;
  portraitOriginalBase64?: string;
  portraitAiVariantsJson?: string;
  faceMatchScore?: number;
  extraImagesJson?: string;
  teacherId?: number | null;
};

export type DvhcItem = {
  id: number;
  maDvhc: string;
  maDVQL: string | null;
  tenDvhc: string;
  tenDayDu: string;
};

export type MethodCount = {
  scanMethod: ScanMethod;
  count: number;
};

export type TtStats = {
  totalCourses: number;
  openCourses: number;
  totalRecords: number;
  recordsToday: number;
  byMethod: MethodCount[];
};

export type BatchItem = {
  fileName: string;
  preview: string;
  status: 'pending' | 'processing' | 'done' | 'error';
  result: CccdScanResult | null;
  batchId?: number;
  /** Link to tt_pending_records.id when this item originated from the
   *  "Chờ lưu hồ sơ" queue. On save, the backend commits to tt_student_record
   *  AND deletes the pending row — atomic via savePendingRecord API. */
  pendingRecordId?: number;
};

export type ExtraImage = {
  base64: string;
  docType: DocType;
  label?: string;
};

export type CccdScanResult = {
  soCCCD: string;
  soCMT: string;
  hoTen: string;
  ngaySinh: string;
  gioiTinh: string;
  diaChi: string;
  diaChiMoi: string;
  ngayCap: string;
  avatarBase64: string | null;
  sourceImageBase64: string | null;
  detectedMethod: ScanMethod | null;
  /** Per-document detect source — CCCD and GPLX may be detected by
   *  different engines in the same record (e.g. CCCD by QR, GPLX by OCR).
   *  Falls back to `detectedMethod` when missing. */
  cccdSource?: 'qr' | 'ocr' | 'manual' | null;
  gplxSource?: 'qr' | 'ocr' | 'manual' | null;
  extraImages: ExtraImage[];
  soGPLX: string;
  hangGPLX: string;
  hangGPLXMoi: string;
  ngayCapGPLX: string;
  noiCapGPLX: string;
  donViCapGPLX: string;
  ngayTrungTuyenGPLX: string;
  ngayHetHanGPLX: string;
  maDvhc: string;
  // Pre-resolved DVHC display name (set during batch OCR so the
  // review screen can show it without re-fetching).
  dvhcTenDayDu?: string;
  // Student-uploaded portrait photo (ảnh đại diện). Kept alongside the
  // CCCD-derived avatarBase64 so the face match can compare them and the
  // operator never overwrites the CCCD crop by mistake.
  portraitBase64?: string | null;
  portraitOriginalBase64?: string | null;
  portraitAiVariantsJson?: string | null;
  faceMatchScore?: number | null;
  /** Supper teacher (groupId=6) phụ trách học viên. NVNL chọn qua dropdown;
   *  supper teacher tự nhập → auto-fill bằng userId chính họ. */
  teacherId?: number | null;
  teacherFullName?: string | null;
};

export type DocDetectResult = {
  docType: DocType | 'portrait' | 'mixed';
  docSide?: 'front' | 'back' | 'unknown';
  /** Public URL of the raw upload persisted by the backend (always present,
   *  even when Mistral classification fails). Serves as a disk-backed
   *  retrieval path so operators can re-access the exact image. */
  savedUrl?: string;
  /** Which engine extracted the fields — surfaced so the operator can
   *  badge the record in the queue and know whether to trust the
   *  extraction (QR = authoritative, OCR = heuristic). */
  detectSource?: 'qr' | 'ocr';
  cccd?: {
    soCCCD: string;
    soCMT: string;
    hoTen: string;
    ngaySinh: string;
    gioiTinh: string;
    queQuan: string;
    diaChi: string;
    noiCuTru: string;
    noiThuongTru: string;
    ngayCap: string;
  };
  gplx?: {
    soGPLX: string;
    hang: string;
    ngayCap: string;
    noiCap: string;
    donViCap: string;
    ngayTrungTuyen: string;
    ngayHetHan: string;
    hoTen: string;
    ngaySinh: string;
  };
  portrait?: boolean;
};

export type QueueRecord = {
  id: string;
  files: File[];
  studentName?: string;
  soCCCD?: string;
  status: 'pending' | 'detecting' | 'done' | 'error';
  currentStep?: string;
  currentStepIndex?: number;
  detectedResult?: CccdScanResult | null;
  error?: string;
  createdAt: Date;
};

export type BatchQueue = {
  records: QueueRecord[];
  totalCount: number;
  processingIndex: number;
  isProcessing: boolean;
};
